import "../../monacoSetup";
import AppIconButton from "../form/AppIconButton";
import { useMemo, useState, type ReactNode } from "react";
import Editor from "@monaco-editor/react";
import { Box, FormHelperText, InputLabel, Stack, Tooltip } from "@mui/material";
import FullscreenRoundedIcon from "@mui/icons-material/FullscreenRounded";
import { useTranslation } from "react-i18next";
import { useMaterialUIController } from "../../context";
import { APP_FORM_DEFAULTS } from "../form/constants";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";
import {
  AppDialog,
  AppDialogActions,
  AppDialogCancelButton,
  AppDialogContent,
  AppDialogPrimaryButton,
  AppDialogTitle,
} from "../dialog";

type Props = {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  height?: number | string;
  helperText?: string;
  error?: boolean;
  readOnly?: boolean;
  language?: "json" | "javascript";
  headerActions?: ReactNode;
  enableFullscreen?: boolean;
  fullscreenTitle?: string;
};

function isValidJson(text: string): boolean {
  if (!text.trim()) {
    return true;
  }
  try {
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
}

export default function JsonCodeEditor({
  label,
  value,
  onChange,
  height = 280,
  helperText,
  error = false,
  readOnly = false,
  language = "json",
  headerActions,
  enableFullscreen = true,
  fullscreenTitle,
}: Props) {
  const { t } = useTranslation();
  const [controller] = useMaterialUIController();
  const custom = useAppCustomStyles();
  const theme = controller.darkMode ? "vs-dark" : "light";
  const borderRadius = APP_FORM_DEFAULTS.variant === "outlined" ? 8 : 4;
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [fullscreenDraft, setFullscreenDraft] = useState(value);

  const editorOptions = useMemo(
    () => ({
      readOnly,
      minimap: { enabled: false },
      fontSize: 13,
      lineNumbers: "on" as const,
      scrollBeyondLastLine: false,
      automaticLayout: true,
      tabSize: 2,
      wordWrap: "on" as const,
      formatOnPaste: true,
      formatOnType: true,
    }),
    [readOnly]
  );

  const openFullscreen = () => {
    setFullscreenDraft(value);
    setFullscreenOpen(true);
  };

  const applyFullscreen = () => {
    onChange(fullscreenDraft);
    setFullscreenOpen(false);
  };

  const fullscreenDraftError =
    language === "json" ? !isValidJson(fullscreenDraft) : false;
  const dialogTitle = fullscreenTitle ?? label ?? t("widget.dslTemplate");

  const fullscreenButton =
    enableFullscreen ? (
      <Tooltip title={t("widget.dslOpenFullscreen")}>
        <AppIconButton
          size="small"
          aria-label={t("widget.dslOpenFullscreen")}
          onClick={openFullscreen}
          sx={custom.components.jsonCodeEditor.fullscreenButton}
        >
          <FullscreenRoundedIcon sx={custom.components.jsonCodeEditor.fullscreenIcon} />
        </AppIconButton>
      </Tooltip>
    ) : null;

  return (
    <>
      <Box sx={custom.components.jsonCodeEditor.root}>
        {label || headerActions || fullscreenButton ? (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            sx={custom.components.jsonCodeEditor.header}
          >
            {label ? (
              <InputLabel shrink error={error} sx={custom.components.jsonCodeEditor.label(error)}>
                {label}
              </InputLabel>
            ) : (
              <span />
            )}
            {headerActions || fullscreenButton ? (
              <Stack direction="row" alignItems="center" spacing={0.25}>
                {headerActions}
                {fullscreenButton}
              </Stack>
            ) : null}
          </Stack>
        ) : null}
        <Box sx={custom.components.jsonCodeEditor.editorShell(error, controller.darkMode, borderRadius)}>
          <Editor
            height={height}
            language={language}
            theme={theme}
            value={value}
            onChange={(v) => onChange(v ?? "")}
            options={editorOptions}
          />
        </Box>
        {helperText ? (
          <FormHelperText error={error} sx={custom.components.jsonCodeEditor.helperText}>
            {helperText}
          </FormHelperText>
        ) : null}
      </Box>

      <AppDialog open={fullscreenOpen} onClose={() => setFullscreenOpen(false)} fullScreen>
        <AppDialogTitle>{dialogTitle}</AppDialogTitle>
        <AppDialogContent sx={custom.components.jsonCodeEditor.fullscreenDialogContent}>
          <Box sx={custom.components.jsonCodeEditor.fullscreenEditorBox}>
            <JsonCodeEditor
              value={fullscreenDraft}
              onChange={setFullscreenDraft}
              readOnly={readOnly}
              language={language}
              error={fullscreenDraftError}
              enableFullscreen={false}
              height="calc(100vh - 180px)"
            />
          </Box>
        </AppDialogContent>
        <AppDialogActions>
          <AppDialogCancelButton onClick={() => setFullscreenOpen(false)}>
            {readOnly ? t("common.close") : t("common.cancel")}
          </AppDialogCancelButton>
          {!readOnly ? (
            <AppDialogPrimaryButton
              onClick={applyFullscreen}
              disabled={fullscreenDraftError}
            >
              {t("common.save")}
            </AppDialogPrimaryButton>
          ) : null}
        </AppDialogActions>
      </AppDialog>
    </>
  );
}
